import { useQueries } from '@tanstack/react-query'
import CustomDialog from 'common/components/CustomDialog'
import { FeatureKey } from 'common/constants/permission'
import { FREE_MODULES } from 'common/constants/permission'
import { getRoleDetailKey } from 'common/constants/queryKeys'
import { GreenSwitch } from 'modules/inventory/components/IsActiveSwitch'
import { getRole } from 'modules/member/api'
import { useMutateCreateRoles, useMutateUpdateRoles } from 'modules/member/api/mutation'
import { useFeatures, useRolesPreset } from 'modules/member/api/selector'
import { MemberPermission, UpdateRolesBody } from 'modules/member/api/types'
import { AccessDialogProps } from 'modules/member/api/types'
import { useUserInfo } from 'modules/user/api/queries'
import { PermissionOption } from 'modules/user/api/types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  useTheme,
  Stack,
  FormControl,
  SelectChangeEvent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'

import './style.css'

export interface CrudState {
  [key: string]: [number, number, number, number]
}

// Used only when a feature's own `options` (from the backend, per DOF-1098)
// is missing/empty — keeps the dialog working during backend rollout.
const getFallbackOptions = (t: (key: string) => string): PermissionOption[] => [
  { label: t('member:access.view'), preset_crud: [0, 1, 0, 0] },
  { label: t('member:access.view.create'), preset_crud: [1, 1, 0, 0] },
  { label: t('member:access.view.create.edit'), preset_crud: [1, 1, 1, 0] },
  { label: t('member:access.view.create.edit.delete'), preset_crud: [1, 1, 1, 1] },
]

const crudKey = (crud: readonly number[]) => crud.join(',')

const BASE_FEATURE_KEYS = [FeatureKey.Farm, FeatureKey.Pond, FeatureKey.Cycle]

const AccessDialogNew = ({ open, onClose, isEdit = false, initialRole }: AccessDialogProps) => {
  const { t } = useTranslation(['common', 'member'])
  const theme = useTheme()
  const title = isEdit ? t('member:edit.manage.role') : t('member:add.manage.role')

  const [name, setName] = useState('')
  const [crudState, setCrudState] = useState<CrudState>({})
  const [role, setRole] = useState('')
  const [permissionSelection, setPermissionSelection] = useState<Record<string, string>>({})
  const [enableAll, setEnableAll] = useState(false)
  const [openSelect, setOpenSelect] = useState<string | null>(null)
  // `options` comes back on each permission entry (sibling of crud_permission),
  // not on the generic feature-list entries — populated from getRole() below.
  const [featureOptionsMap, setFeatureOptionsMap] = useState<Record<string, PermissionOption[]>>({})

  const { data: features } = useFeatures()
  const { data: rolesPreset } = useRolesPreset()
  const { data: userInfo } = useUserInfo()
  const createRolesMutation = useMutateCreateRoles()
  const updateRolesMutation = useMutateUpdateRoles()
  const FALLBACK_OPTIONS = getFallbackOptions(t)

  // Add mode has no roleId yet, so getRole() below never runs for it —
  // meaning featureOptionsMap stays empty and every row would otherwise
  // fall back to the generic FALLBACK_OPTIONS. `options` describes a
  // *feature's* possible permission levels, not a role's, so any existing
  // role's detail carries the correct options for the features it lists.
  // Harvest them from the preset roles (already fetched for every account)
  // via TanStack Query rather than an ad-hoc fetch, so they're cached and
  // deduped like any other server data.
  const presetRoleQueries = useQueries({
    queries: (isEdit ? [] : rolesPreset ?? []).map((preset) => ({
      queryKey: getRoleDetailKey(userInfo?.id, preset.id),
      queryFn: async () => {
        const { data } = await getRole({ userId: userInfo!.id, roleId: preset.id })
        return data.data
      },
      enabled: !!userInfo?.id,
      staleTime: Infinity,
    })),
  })

  const presetOptionsMap: Record<string, PermissionOption[]> = {}
  presetRoleQueries.forEach((query) => {
    query.data?.permissions.forEach(({ feature: { key }, options }) => {
      if (!presetOptionsMap[key]?.length && options?.length) presetOptionsMap[key] = options
    })
  })

  const getOptionsForFeature = (featureKey: string): PermissionOption[] => {
    if (featureOptionsMap[featureKey]?.length) return featureOptionsMap[featureKey]
    const feature = features?.find((f) => f.key === featureKey)
    if (feature?.options?.length) return feature.options
    if (presetOptionsMap[featureKey]?.length) return presetOptionsMap[featureKey]
    return FALLBACK_OPTIONS
  }

  const ensureBaseFeaturesHaveRead = (newCrudState: CrudState, featureKey: FeatureKey) => {
    if (!FREE_MODULES.includes(featureKey) && newCrudState[featureKey]?.[1] === 1) {
      BASE_FEATURE_KEYS.forEach((k) => {
        if (!newCrudState[k]) newCrudState[k] = [0, 0, 0, 0]
        if (newCrudState[k][1] === 0) newCrudState[k][1] = 1
      })
    }
  }

  const applyOptionCrudKeyToCrud = (featureKey: string, optionCrudKey: string, dest: CrudState) => {
    const options = getOptionsForFeature(featureKey)
    const option = options.find((o) => crudKey(o.preset_crud) === optionCrudKey)
    dest[featureKey] = option ? [...option.preset_crud] : [0, 1, 0, 0]
  }

  const HIERARCHY: FeatureKey[] = [FeatureKey.Farm, FeatureKey.Pond, FeatureKey.Cycle]

  const handleRowToggle = (featureKey: FeatureKey, checked: boolean) => {
    const newState = { ...crudState }
    const newSelection = { ...permissionSelection }

    const turnOn = (key: FeatureKey) => {
      const defaultCrudKey = crudKey(getOptionsForFeature(key)[0]?.preset_crud ?? [0, 1, 0, 0])
      const sel = newSelection[key] ?? defaultCrudKey
      applyOptionCrudKeyToCrud(key, sel, newState)
      // A feature that was already listed on the role with no crud (e.g.
      // [0,0,0,0]) seeds permissionSelection with that key on load. That key
      // matches no real dropdown option, so re-deriving it from whatever
      // applyOptionCrudKeyToCrud actually applied keeps the dropdown showing
      // a real label instead of "-" after switching the row back on.
      newSelection[key] = crudKey(newState[key])
    }

    const turnOff = (key: FeatureKey) => {
      newState[key] = [0, 0, 0, 0]
    }

    const isHierarchy = HIERARCHY.includes(featureKey)
    const index = HIERARCHY.indexOf(featureKey)
    // DOF-1278: IoT (and any other FREE_MODULES entry) is independently
    // grantable - it must not participate in the Farm/Pond/Cycle prerequisite
    // dance, in either direction.
    const subFeatures =
      features
        ?.filter((f) => !HIERARCHY.includes(f.key) && !FREE_MODULES.includes(f.key))
        .map((f) => f.key) || []

    if (checked) {
      turnOn(featureKey)

      if (isHierarchy) {
        for (let i = 0; i < index; i++) {
          turnOn(HIERARCHY[i])
        }
      } else if (!FREE_MODULES.includes(featureKey)) {
        HIERARCHY.forEach((key) => turnOn(key))
      }
    } else {
      turnOff(featureKey)

      if (isHierarchy) {
        for (let i = index + 1; i < HIERARCHY.length; i++) {
          turnOff(HIERARCHY[i])
        }
        subFeatures.forEach((key) => turnOff(key))
      } else if (!FREE_MODULES.includes(featureKey)) {
        const anySubOn = subFeatures.some((key) => newState[key]?.some((v) => v === 1))
        if (!anySubOn) {
          HIERARCHY.forEach((key) => turnOff(key))
        }
      }
    }

    const allFeatures = [...HIERARCHY, ...subFeatures]
    const allOn = allFeatures.every((k) => newState[k]?.some((v) => v === 1))
    const allOff = allFeatures.every((k) => !newState[k]?.some((v) => v === 1))

    setEnableAll(allOn)
    setCrudState(newState)
    setPermissionSelection(newSelection)
  }

  const handlePermissionSelect = (featureKey: FeatureKey, value: string) => {
    const nextSelection = { ...permissionSelection, [featureKey]: value }
    setPermissionSelection(nextSelection)

    if (crudState[featureKey]?.[1] === 1) {
      const newState = { ...crudState }
      applyOptionCrudKeyToCrud(featureKey, value, newState)
      ensureBaseFeaturesHaveRead(newState, featureKey)
      setCrudState(newState)
    }
  }

  const handleEnableAllToggle = (checked: boolean) => {
    setEnableAll(checked)
    if (!features) return

    const newState = { ...crudState }
    const newSelection = { ...permissionSelection }
    if (checked) {
      features.forEach((f) => {
        const key = f.key
        const hasValidCrud = newState[key]?.some((v) => v === 1)
        const defaultCrudKey = crudKey(getOptionsForFeature(key)[0]?.preset_crud ?? [0, 1, 0, 0])
        const sel =
          newSelection[key] ?? (hasValidCrud ? crudKey(newState[key]) : defaultCrudKey)
        applyOptionCrudKeyToCrud(key, sel, newState)
        ensureBaseFeaturesHaveRead(newState, key)
        // Same stale-selection issue as handleRowToggle: re-derive from what
        // was actually applied so a feature listed with no crud on load
        // doesn't leave the dropdown showing "-".
        newSelection[key] = crudKey(newState[key])
      })
    } else {
      features.forEach((f) => {
        newState[f.key] = [0, 0, 0, 0]
      })
    }
    setCrudState(newState)
    setPermissionSelection(newSelection)
  }

  const onSave = async () => {
    const newCrud = { ...crudState }

    const fullState = features?.reduce((acc, feature) => {
      const featureKey = feature.key
      if (!FREE_MODULES.includes(featureKey) && newCrud[featureKey]?.[1] === 1) {
        BASE_FEATURE_KEYS.forEach((k) => {
          if (!newCrud[k]) newCrud[k] = [0, 0, 0, 0]
          if (newCrud[k][1] === 0) newCrud[k][1] = 1
        })
      }
      acc[featureKey] = newCrud[featureKey] || [0, 0, 0, 0]
      return acc
    }, {} as CrudState)

    const formatCrud = (
      input: Record<string, [number, number, number, number]>
    ): MemberPermission[] =>
      Object.keys(input).map((feature_name) => ({
        feature_name,
        crud_permission: input[feature_name],
      }))

    if (!userInfo) return

    if (!isEdit) {
      await createRolesMutation
        .mutateAsync({
          userId: userInfo.id,
          body: { name, permissions: formatCrud(fullState!) } as UpdateRolesBody,
        })
        .finally(() => handleOnClose())
    } else {
      await updateRolesMutation
        .mutateAsync({
          userId: userInfo.id,
          roleId: initialRole!.id,
          body: { name, permissions: formatCrud(fullState!) } as UpdateRolesBody,
        })
        .finally(() => handleOnClose())
    }
  }

  const handleOnClose = () => {
    setName('')
    setCrudState({})
    setRole('')
    setPermissionSelection({})
    setFeatureOptionsMap({})
    setEnableAll(false)
    onClose()
  }

  const isRowShown = (key: string) => crudState[key]?.[1] === 1

  useEffect(() => {
    if (!userInfo?.id || !role || role === '-' || role === '') {
      setCrudState({})
      setPermissionSelection({})
      setFeatureOptionsMap({})
      setEnableAll(false)
      return
    }

    getRole({ userId: userInfo.id, roleId: role }).then((res) => {
      const permissions = res.data.data.permissions
      const newCrudState: CrudState = {}
      const newSelection: Record<string, string> = {}
      const newOptionsMap: Record<string, PermissionOption[]> = {}

      permissions.forEach(({ feature: { key }, crud_permission, options }) => {
        newCrudState[key] = crud_permission || [0, 0, 0, 0]
        newSelection[key] = crudKey(newCrudState[key])
        if (options?.length) newOptionsMap[key] = options
      })

      setCrudState(newCrudState)
      setPermissionSelection(newSelection)
      setFeatureOptionsMap(newOptionsMap)
      setEnableAll(Object.keys(newCrudState).every((k) => newCrudState[k]?.some((v) => v === 1)))
    })
  }, [role, userInfo])

  useEffect(() => {
    if (isEdit && initialRole) {
      setName(initialRole.name ?? '')
      setRole(initialRole.id)
    } else {
      setName('')
    }
  }, [isEdit, initialRole])

  return (
    <CustomDialog
      open={open}
      title={title}
      contentMinWidth={500}
      closeDialog={handleOnClose}
      enableCloseButton
      enableInLineWithTitle
      submitLabel={t('common:submit')}
      formName="create-access-dialog"
      onSubmit={onSave}
    >
      <form id="member-detail-dialog">
        <Box>
          <Box sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 400, fontSize: 16 }}>
              <Box
                component="span"
                sx={{ fontWeight: 400, fontSize: 16, color: 'error.main', mr: 0.5 }}
              >
                *
              </Box>
              {t('member:table.position')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="text"
              placeholder={t('member:role.dialog.require.input')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>

          <Stack
            direction="row"
            px={1}
            py={0.9}
            gap={1}
            alignItems="center"
            sx={{
              backgroundColor: 'info.background',
              borderRadius: 0.5,
              py: 0.5,
              px: 1,
              border: '1px solid',
              borderColor: 'info.border',
              mt: 0.5,
            }}
          >
            <Typography variant="caption" color="info.main">
              {t('member:access.important.note')}
            </Typography>
          </Stack>
          <Divider sx={{ my: 2, borderColor: 'divider' }} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 2,
              mb: 3,
            }}
          >
            <Typography fontWeight={600} fontSize={18}>
              {t('member:manage.role')}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <GreenSwitch
                checked={!!enableAll}
                onChange={(_, checked) => handleEnableAllToggle(checked)}
              />
              <Typography variant="subtitle1" fontSize={16}>
                {t('member:open.all.access')}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2, maxHeight: 600, overflowY: 'auto' }}
            >
              <Table
                aria-label="access-permission-table"
                stickyHeader
                sx={{
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  '& thead th': {
                    fontSize: '16px',
                    color: 'text.primary',
                    backgroundColor: '#F7F7F7',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                  '& tbody td': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                  '& thead th:first-of-type': { borderTopLeftRadius: 12 },
                  '& thead th:last-of-type': { borderTopRightRadius: 12 },
                  '& tbody tr:last-of-type td:first-of-type': { borderBottomLeftRadius: 12 },
                  '& tbody tr:last-of-type td:last-of-type': { borderBottomRightRadius: 12 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: 20 }}>
                      {t('member:table.access')}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, fontSize: 20, width: 120, verticalAlign: 'middle' }}
                    >
                      {t('member:table.show.hide')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 20, width: 220 }}>
                      {t('member:manage.role')}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {features?.map((feature) => {
                    const key = feature.key
                    const label = feature.name_th ?? key
                    const options = getOptionsForFeature(key)
                    const selectedCrudKey =
                      permissionSelection[key] ?? crudKey(crudState[key] ?? [0, 0, 0, 0])

                    return (
                      <TableRow key={key} hover>
                        <TableCell>
                          <Typography>{label}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <GreenSwitch
                            checked={!!isRowShown(key)}
                            onChange={(_, checked) => handleRowToggle(key, checked)}
                          />
                        </TableCell>

                        <TableCell>
                          {isRowShown(key) ? (
                            <FormControl size="small" fullWidth sx={{ m: 0 }}>
                              <Select
                                className="member-select"
                                open={openSelect === key}
                                onOpen={() => setOpenSelect(key)}
                                onClose={() => setOpenSelect(null)}
                                value={selectedCrudKey}
                                onChange={(e: SelectChangeEvent<string>) =>
                                  handlePermissionSelect(key, e.target.value)
                                }
                                displayEmpty
                                renderValue={(value) => {
                                  const option = options.find(
                                    (o) => crudKey(o.preset_crud) === value
                                  )

                                  return (
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 4,
                                      }}
                                    >
                                      <span>{option ? option.label : '-'}</span>
                                      {openSelect === key ? (
                                        <ExpandLessIcon fontSize="small" />
                                      ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                      )}
                                    </div>
                                  )
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                  '& .MuiSelect-select': {
                                    color: theme.palette.primary.main,
                                    paddingLeft: 0,
                                  },
                                  backgroundColor: 'transparent',
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      '& .MuiMenuItem-root': { color: theme.palette.text.primary },
                                    },
                                  },
                                }}
                              >
                                {options.map((opt) => (
                                  <MenuItem
                                    key={crudKey(opt.preset_crud)}
                                    value={crudKey(opt.preset_crud)}
                                  >
                                    <Typography>{opt.label}</Typography>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Typography sx={{ color: theme.palette.text.primary }}>
                              {t('member:no.access')}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  }) || (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography sx={{ p: 2 }}>{t('common:loading')}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </form>
    </CustomDialog>
  )
}

export default AccessDialogNew
